import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { WaitlistSignup } from './pages/WaitlistSignup';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Onboarding } from './pages/Onboarding';
import { Pricing } from './pages/Pricing';
import { Partnerships } from './pages/Partnerships';
import { SwipePage } from './pages/JobSeeker/SwipePage';
import { Discover } from './pages/JobSeeker/Discover';
import { CreditsStore } from './pages/JobSeeker/CreditsStore';
import { ResumeGlowUp } from './pages/JobSeeker/ResumeGlowUp';
import { EnhancedResumeBuilder } from './pages/JobSeeker/EnhancedResumeBuilder';
import { TemplateGallery } from './pages/JobSeeker/TemplateGallery';
import { InterviewFastPass } from './pages/JobSeeker/InterviewFastPass';
import { ProfileVisibilityBoost } from './pages/JobSeeker/ProfileVisibilityBoost';
import { CourseCatalog } from './pages/JobSeeker/CourseCatalog';
import { VerifiedBadge } from './pages/JobSeeker/VerifiedBadge';
import { AIAssistant } from './pages/JobSeeker/AIAssistant';
import { MyApplications } from './pages/JobSeeker/MyApplications';
import { MyResumes } from './pages/JobSeeker/MyResumes';
import { JobSeekerMessages } from './pages/JobSeeker/JobSeekerMessages';
import { Profile } from './pages/JobSeeker/Profile';
import { Settings as JobSeekerSettings } from './pages/JobSeeker/Settings';
import { Settings as EmployerSettings } from './pages/Employer/Settings';
import { Settings as AdminSettings } from './pages/Admin/Settings';
import { Referrals } from './pages/JobSeeker/Referrals';
import { GamificationDashboard } from './pages/JobSeeker/GamificationDashboard';
import { GigBoard } from './pages/JobSeeker/GigBoard';
import { PersonalityAssessment } from './pages/JobSeeker/PersonalityAssessment';
import { PortfolioBuilder } from './pages/JobSeeker/PortfolioBuilder';
import { SubscriptionPricing } from './pages/SubscriptionPricing';
import { EmployerDashboard } from './pages/Employer/Dashboard';
import { JobsManagement } from './pages/Employer/JobsManagement';
import { ApplicationsReview } from './pages/Employer/ApplicationsReview';
import { CompanyProfile } from './pages/Employer/CompanyProfile';
import { EmployerMessages } from './pages/Employer/EmployerMessages';
import { PriorityVisibility } from './pages/Employer/PriorityVisibility';
import { AdminSeed } from './pages/AdminSeed';
import { AdminDashboard } from './pages/Admin/Dashboard';
import { AdminUsers } from './pages/Admin/Users';
import { AdminFinancials } from './pages/Admin/Financials';
import { AdminAnalytics } from './pages/Admin/Analytics';
import { AdminSetup } from './pages/AdminSetup';
import { CreditsManagement } from './pages/Admin/CreditsManagement';
import { JobsManagement as AdminJobsManagement } from './pages/Admin/JobsManagement';
import { FloatingAIAssistant } from './components/FloatingAIAssistant';
import { FloatingBackButton } from './components/FloatingBackButton';
import { AccessibilityProvider, SkipToMainContent } from './components/AccessibilityProvider';
import { AccessibilityMenu } from './components/AccessibilityMenu';
import NotFound from './pages/NotFound';
import Notifications from './pages/JobSeeker/Notifications';
import JobPostingForm from './pages/Employer/JobPostingForm';

function App() {
  const { initialize, initialized, user, profile, signOut } = useAuthStore();

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    // Timeout to prevent infinite loading if profile fails to load
    if (initialized && user && !profile) {
      const timeout = setTimeout(() => {
        console.error('[App] Profile loading timeout - signing out user');
        signOut();
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [initialized, user, profile, signOut]);

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AccessibilityProvider>
        <BrowserRouter>
          <SkipToMainContent />
          <Routes>
        <Route
          path="/"
          element={
            user ? (
              profile ? (
                profile.role === 'admin' ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : profile.role === 'employer' ? (
                  <Navigate to="/employer/dashboard" replace />
                ) : (
                  <Navigate to="/swipe" replace />
                )
              ) : (
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-white to-amber-50">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading your profile...</p>
                    <p className="text-xs text-gray-500 mt-2">If this takes too long, please refresh the page</p>
                  </div>
                </div>
              )
            ) : (
              <Landing />
            )
          }
        />

        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<WaitlistSignup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/subscription-pricing" element={<SubscriptionPricing />} />
        <Route path="/partnerships" element={<Partnerships />} />
        <Route
          path="/admin/seed"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminSeed />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/setup"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminSetup />
            </ProtectedRoute>
          }
        />

        <Route
          path="/swipe"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <SwipePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/discover"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <Discover />
            </ProtectedRoute>
          }
        />

        <Route
          path="/credits"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <CreditsStore />
            </ProtectedRoute>
          }
        />

        <Route
          path="/resume-glowup"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <ResumeGlowUp />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/resume-builder"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <EnhancedResumeBuilder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/templates"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <TemplateGallery />
            </ProtectedRoute>
          }
        />


        <Route
          path="/interview-fastpass"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <InterviewFastPass />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile-visibility-boost"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <ProfileVisibilityBoost />
            </ProtectedRoute>
          }
        />

        <Route
          path="/courses"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <CourseCatalog />
            </ProtectedRoute>
          }
        />

        <Route
          path="/verified-badge"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <VerifiedBadge />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ai-assistant"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <AIAssistant />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/applications"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <MyApplications />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/resumes"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <MyResumes />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/messages"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <JobSeekerMessages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/profile"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <Profile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/settings"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <JobSeekerSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/referrals"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <Referrals />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/gamification"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <GamificationDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/gigs"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <GigBoard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/personality-assessment"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <PersonalityAssessment />
            </ProtectedRoute>
          }
        />

        <Route
          path="/jobseeker/portfolio"
          element={
            <ProtectedRoute requireRole="job_seeker">
              <PortfolioBuilder />
            </ProtectedRoute>
          }
        />

        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/dashboard"
          element={
            <ProtectedRoute requireRole="employer">
              <EmployerDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/profile"
          element={
            <ProtectedRoute requireRole="employer">
              <CompanyProfile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs"
          element={
            <ProtectedRoute requireRole="employer">
              <JobsManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/applications"
          element={
            <ProtectedRoute requireRole="employer">
              <ApplicationsReview />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/messages"
          element={
            <ProtectedRoute requireRole="employer">
              <EmployerMessages />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/priority"
          element={
            <ProtectedRoute requireRole="employer">
              <PriorityVisibility />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/jobs/new"
          element={
            <ProtectedRoute requireRole="employer">
              <JobPostingForm />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employer/settings"
          element={
            <ProtectedRoute requireRole="employer">
              <EmployerSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/dashboard"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminUsers />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/financials"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminFinancials />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminAnalytics />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/credits"
          element={
            <ProtectedRoute requireRole="admin">
              <CreditsManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/jobs"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminJobsManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute requireRole="admin">
              <AdminSettings />
            </ProtectedRoute>
          }
        />

        {/* 404 Catch-All Route */}
        <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Global AI Assistant - Available to all authenticated users */}
        {user && profile && (
          <FloatingAIAssistant />
        )}

        {/* Global Floating Back Button - Available on all pages except landing/auth */}
        <FloatingBackButton />

        {/* Global Accessibility Menu */}
        <AccessibilityMenu />
        </BrowserRouter>
      </AccessibilityProvider>
    </ErrorBoundary>
  );
}

export default App;
